import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

export type TooltipPlacement =
  | 'auto'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'left-top'
  | 'left-bottom';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  placement?: TooltipPlacement;
  delay?: number;
  disabled?: boolean;
  className?: string;
  offset?: number;
  zIndex?: number;
  offsetX?: number;
  offsetY?: number;
  /** Where to portal the tooltip. Defaults to the nearest <dialog> ancestor of
   *  the trigger, falling back to document.body. Native modal dialogs (the
   *  Drawer component) live in the browser's TOP LAYER, which paints above
   *  everything in document.body no matter how high the z-index — so a tooltip
   *  portaled to document.body renders BEHIND an open drawer. */
  portalContainer?: HTMLElement | null;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = 'top',
  delay = 100,
  disabled = false,
  className = '',
  offset = 8,
  zIndex = 9999,
  offsetX = 0,
  offsetY = 0,
  portalContainer,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    // Find the actual element inside the trigger (button, link, span, or first child)
    const targetElement = triggerRef.current.querySelector('a, button, span') ||
                          triggerRef.current.firstElementChild ||
                          triggerRef.current;
    const triggerRect = targetElement.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();


    let top = 0;
    let left = 0;

    // Determine actual placement (auto-detect best side if 'auto')
    let actualPlacement = placement;
    if (placement === 'auto') {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate available space on each side
      const spaceTop = triggerRect.top;
      const spaceBottom = viewportHeight - triggerRect.bottom;
      const spaceLeft = triggerRect.left;
      const spaceRight = viewportWidth - triggerRect.right;

      // Find the side with the most space that can fit the tooltip
      const spaces = [
        { side: 'top' as const, space: spaceTop, fits: spaceTop >= tooltipRect.height + offset },
        { side: 'bottom' as const, space: spaceBottom, fits: spaceBottom >= tooltipRect.height + offset },
        { side: 'left' as const, space: spaceLeft, fits: spaceLeft >= tooltipRect.width + offset },
        { side: 'right' as const, space: spaceRight, fits: spaceRight >= tooltipRect.width + offset },
      ];

      // Prefer sides that fit, sorted by available space
      const fitting = spaces.filter(s => s.fits).sort((a, b) => b.space - a.space);
      if (fitting.length > 0) {
        actualPlacement = fitting[0].side;
      } else {
        // Nothing fits perfectly, pick the side with most space
        const best = spaces.sort((a, b) => b.space - a.space)[0];
        actualPlacement = best.side;
      }
    }

    // Calculate base position based on placement (using viewport coordinates)
    switch (actualPlacement) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'top-start':
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left;
        break;
      case 'top-end':
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.right - tooltipRect.width;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + offset;
        break;
      case 'right-start':
        top = triggerRect.top;
        left = triggerRect.right + offset;
        break;
      case 'right-end':
        top = triggerRect.bottom - tooltipRect.height;
        left = triggerRect.right + offset;
        break;
      case 'bottom':
        top = triggerRect.bottom + offset;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom-start':
        top = triggerRect.bottom + offset;
        left = triggerRect.left;
        break;
      case 'bottom-end':
        top = triggerRect.bottom + offset;
        left = triggerRect.right - tooltipRect.width;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - offset;
        break;
      case 'left-start':
        top = triggerRect.top;
        left = triggerRect.left - tooltipRect.width - offset;
        break;
      case 'left-end':
        top = triggerRect.bottom - tooltipRect.height;
        left = triggerRect.left - tooltipRect.width - offset;
        break;
      case 'left-top':
        // Align to left, but with top edge of tooltip aligned with top of trigger
        top = triggerRect.top;
        left = triggerRect.left - tooltipRect.width - offset;
        break;
      case 'left-bottom':
        // Align to left, but with bottom edge of tooltip aligned with bottom of trigger
        top = triggerRect.bottom - tooltipRect.height;
        left = triggerRect.left - tooltipRect.width - offset;
        break;
    }

    // Apply custom offsets
    top += offsetY;
    left += offsetX;

    // Viewport collision detection - keep tooltip within viewport bounds
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8; // Minimum distance from viewport edge

    // Check right edge
    if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding;
    }
    // Check left edge
    if (left < padding) {
      left = padding;
    }
    // Check bottom edge
    if (top + tooltipRect.height > viewportHeight - padding) {
      top = viewportHeight - tooltipRect.height - padding;
    }
    // Check top edge
    if (top < padding) {
      top = padding;
    }

    setPosition({ top, left });
  };

  const handleMouseEnter = () => {
    if (disabled) return;

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  // Decide visibility from where the pointer actually is, not only from React's
  // onMouseLeave. That handler is synthesized from the native `mouseout` event,
  // and Chrome never fires `mouseout` for a node that was detached and
  // re-inserted while hovered — a keyed row reorder on a live-refreshing grid
  // (Dispatch Planner, Search Results) does exactly that. The next boundary
  // event is attributed to the removed node's PARENT, so the trigger never
  // hears the pointer leave and the tooltip stays on screen forever (the
  // stacked "PRO #" tooltips seen floating over the Pro drawer). While visible,
  // watch pointer movement at the document level and hide as soon as the
  // pointer is over anything outside the trigger; also hide on pointerdown so
  // the click that opens a drawer does not leave a tooltip behind under it.
  // Listeners exist only while a tooltip is showing, so idle cost is zero.
  useEffect(() => {
    if (!isVisible) return;

    const hide = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsVisible(false);
    };
    const hideIfPointerOutside = (e: Event) => {
      const trigger = triggerRef.current;
      const target = e.target as Node | null;
      if (!trigger || !trigger.isConnected || !target || !trigger.contains(target)) {
        hide();
      }
    };

    document.addEventListener('pointermove', hideIfPointerOutside, true);
    document.addEventListener('pointerdown', hide, true);
    return () => {
      document.removeEventListener('pointermove', hideIfPointerOutside, true);
      document.removeEventListener('pointerdown', hide, true);
    };
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      // Small delay to ensure tooltip is rendered before calculating position
      setTimeout(() => {
        calculatePosition();
      }, 0);

      window.addEventListener('scroll', calculatePosition);
      window.addEventListener('resize', calculatePosition);
    }

    return () => {
      window.removeEventListener('scroll', calculatePosition);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!content) return <>{children}</>;

  // Explicit container always wins; otherwise mount into the enclosing native
  // <dialog> (top layer) so the tooltip is not painted behind an open drawer.
  const target =
    portalContainer ||
    (triggerRef.current?.closest('dialog') as HTMLElement | null) ||
    document.body;

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'contents' }}
      >
        {children}
      </div>
      {isVisible && createPortal(
        <div
          ref={tooltipRef}
          className={`${styles.tooltip} ${styles[placement]} ${className}`}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: zIndex,
          }}
        >
          {content}
          <div className={`${styles.arrow} ${styles[`arrow-${placement}`]}`} />
        </div>,
        target
      )}
    </>
  );
};